import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import { DismissRegular, SearchRegular } from "@fluentui/react-icons";
import { DynamicFieldsApi } from "@/services/api/dynamic-fields-api";
import type { PeoplePickerUser } from "@/types/models";
import { parsePeoplePickerIds, serializePeoplePickerIds } from "./deal-dynamic-fields";

export interface DealPeoplePickerProps {
  fieldId: number;
  /** JSON array of UserIds, e.g. "[11,12]". */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
}

/** Inactive and archived users stay visible but can never be re-selected. */
function isStale(user: PeoplePickerUser): boolean {
  return user.Archived === true || user.Active === false;
}

function staleLabel(user: PeoplePickerUser): string {
  return user.Archived === true ? "Archived" : "Inactive";
}

function metaLine(user: PeoplePickerUser): string {
  return [user.SubDepartment, user.Role].filter(Boolean).join(" · ");
}

/**
 * People Picker input for a dynamic field. Mirrors Triyo.Web's TaskPeoplePicker
 * value contract (a JSON UserId array) and its stale-user rules, but renders the
 * candidate list inline rather than in a floating dropdown — a task pane is an
 * overflow container that would clip one.
 */
export function DealPeoplePicker({
  fieldId,
  value,
  onChange,
  disabled = false,
  invalid = false,
  describedBy,
}: DealPeoplePickerProps): JSX.Element {
  const [eligible, setEligible] = useState<PeoplePickerUser[]>([]);
  const [resolved, setResolved] = useState<Record<number, PeoplePickerUser>>({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const requestRef = useRef(0);

  const selectedIds = useMemo(() => parsePeoplePickerIds(value), [value]);

  // Fetched once per field: `value` is read for the initial resolution only, so
  // later selections resolve from the list already loaded rather than refetching.
  useEffect(() => {
    const requestId = ++requestRef.current;
    setLoading(true);
    DynamicFieldsApi.getPeoplePickerRoster(fieldId, parsePeoplePickerIds(value))
      .then((roster) => {
        if (requestId !== requestRef.current) return;
        const eligibleUsers = roster.EligibleUsers ?? [];
        const byId: Record<number, PeoplePickerUser> = {};
        for (const user of eligibleUsers) byId[user.Id] = user;
        for (const user of roster.SelectedUsers ?? []) byId[user.Id] = user;
        setEligible(eligibleUsers);
        setResolved(byId);
      })
      .catch(() => {
        if (requestId === requestRef.current) setEligible([]);
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, [fieldId]);

  const userInfo = (id: number): PeoplePickerUser =>
    resolved[id] ?? { Id: id, Name: `User ${id}`, Active: false, Archived: true };

  const emit = (ids: number[]) => onChange(serializePeoplePickerIds(ids));

  const toggle = (id: number) => {
    emit(selectedIds.includes(id) ? selectedIds.filter((entry) => entry !== id) : [...selectedIds, id]);
  };

  const search = query.trim().toLowerCase();
  const suggestions = eligible.filter((user) => !search || (user.Name ?? "").toLowerCase().includes(search));

  // Stale selections are not in the eligible list, so surface them at the top —
  // removable there, but once removed they cannot be added back.
  const staleSelected = selectedIds
    .map(userInfo)
    .filter((user) => isStale(user) && !eligible.some((entry) => entry.Id === user.Id))
    .filter((user) => !search || (user.Name ?? "").toLowerCase().includes(search));

  const listId = `create-deal-dynamic-${fieldId}-people`;

  return (
    <div className={`create-deal__people${invalid ? " create-deal__people--invalid" : ""}`}>
      {selectedIds.length > 0 ? (
        <div className="create-deal__people-chips">
          {selectedIds.map((id) => {
            const user = userInfo(id);
            return (
              <span
                key={id}
                className={`create-deal__people-chip${isStale(user) ? " create-deal__people-chip--stale" : ""}`}
              >
                {user.Name}
                {isStale(user) ? <span className="create-deal__people-chip-badge">{staleLabel(user)}</span> : null}
                <button type="button" aria-label={`Remove ${user.Name}`} disabled={disabled} onClick={() => toggle(id)}>
                  <DismissRegular />
                </button>
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="create-deal__search create-deal__people-search">
        <SearchRegular />
        <input
          id={`create-deal-dynamic-${fieldId}`}
          value={query}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          placeholder="Search users"
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {loading ? (
        <p className="create-deal__people-empty">Loading users…</p>
      ) : suggestions.length === 0 && staleSelected.length === 0 ? (
        <p className="create-deal__people-empty">{eligible.length === 0 ? "No users available." : "No matches"}</p>
      ) : (
        <div className="create-deal__people-list" role="group" aria-label="People options" id={listId}>
          {staleSelected.map((user) => (
            <label key={`stale-${user.Id}`} className="create-deal__people-row create-deal__people-row--stale">
              <input type="checkbox" checked disabled={disabled} onChange={() => toggle(user.Id)} />
              <span className="create-deal__people-text">
                <span className="create-deal__people-name">
                  {user.Name}
                  <span className="create-deal__people-chip-badge">{staleLabel(user)}</span>
                </span>
                <span className="create-deal__people-meta">Cannot be re-selected once removed</span>
              </span>
            </label>
          ))}

          {suggestions.map((user) => {
            const meta = metaLine(user);
            return (
              <label key={user.Id} className="create-deal__people-row">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(user.Id)}
                  disabled={disabled}
                  onChange={() => toggle(user.Id)}
                />
                <span className="create-deal__people-text">
                  <span className="create-deal__people-name">{user.Name}</span>
                  {meta ? <span className="create-deal__people-meta">{meta}</span> : null}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
